import { CreateJobDTO, Job } from '@/apis/recruitementApis'
import Button from '@/common/button'
import FormGroup from '@/common/Input/FormGroup'
import InputWithAdornment from '@/common/Input/InputWithAdornment'
import SelectInput from '@/common/Input/SelectInput'
import SelectInputV2 from '@/common/Input/SelectInput.v2'
import Textarea from '@/common/Input/Textarea'
import Modal from '@/common/modal/Modal'
import ModalHeader from '@/common/modal/ModalHeader'
import { ToastNotify } from '@/common/toastManager'
import { withAsync } from '@/helpers/withAsync'
import { useAppSelector } from '@/store/hooks'
import {
  useDeleteJobImageMutation,
  useJobFiltersQuery,
  useJobPipelinesQuery,
  useRecruiterCreateJobMutation,
  useUpdateRecruiterJobMutation,
} from '@/store/recruitementSlice'
import { FormikHelpers, useFormik } from 'formik'
import { useMemo } from 'react'
import jobSchema from '../validators/job.validator'
import { useTranslation } from 'react-i18next'

import UploadImage from '@/pages/profile/alumni/portfolio/createupdate/UploadImage'
import { prepareImage } from '@/pages/profile/alumni/portfolio/createupdate'
import ToggleSwitch from '@/common/Input/ToggleSwitch'
import Typography from '@/common/Typography'

type IOpenable = {
  open: boolean
  onClose: () => void
  data?: Job
  type?: 'internship' | 'others'
}

const CreateOrUpdateJob = (props: IOpenable) => {
  const { open, onClose, data, type = 'others' } = props

  const authUser = useAppSelector((state: any) => state.auth)
  const { data: allFilters } = useJobFiltersQuery()

  const { data: paginatedPipelines, isLoading: isFetchingPipelines } =
    useJobPipelinesQuery({ page: 1 })
  const [createJob] = useRecruiterCreateJobMutation()
  const [updateJob] = useUpdateRecruiterJobMutation()
  const [deleteImage] = useDeleteJobImageMutation()
  const initialData: CreateJobDTO = useMemo(() => getInitialData(data), [data])
  initialData.pipeline = paginatedPipelines?.results?.[0]?.id!
  const submitHandler = async (
    values: CreateJobDTO,
    { setSubmitting, resetForm }: FormikHelpers<CreateJobDTO>
  ) => {
    const { newImages, deletedImages } = prepareImage(
      values.images as any,
      (initialData.images || []) as any
    )

    const defaultImage = newImages.findIndex((image) => image.default)
    if (defaultImage > -1) {
      newImages.unshift({ ...newImages[defaultImage] })
      newImages.splice(defaultImage, 1)
    }

    const fd = new FormData()
    const finalObj = data
      ? {
          ...values,
          id: data.id,
          company: (authUser as any).company.id as number,
        }
      : {
          ...values,
          company: (authUser as any).company.id as number,
        }
    Object.entries(finalObj)
      .filter(
        ([key]) =>
          ![
            'images',
            'date_deleted',
            ...(type === 'internship' ? ['pipeline'] : []),
          ].includes(key)
      )
      .forEach(([key, value]) => {
        if (value !== '') fd.set(key, value as string)
      })
    newImages.forEach((img, idx) => {
      fd.set(`image${idx}`, img.file as File)
    })
    if (type === 'internship') {
      fd.set('job_type', 'Internship')
    }

    const { response, error } = await withAsync(
      () => (data ? updateJob(fd).unwrap() : createJob(fd).unwrap()),
      () => setSubmitting(false)
    )
    if (error) {
      ToastNotify('error', {
        message: (error as any).message || 'Ooops...Could not create job',
      })
      return
    }
    if (!response) return
    await withAsync(() =>
      Promise.all(
        deletedImages.map((image) => {
          return deleteImage({ id: image.id || 0 }).unwrap()
        })
      )
    )
    ToastNotify('success', {
      message:
        type === 'internship'
          ? data
            ? 'Internship updated'
            : 'internship posted'
          : (response as any).message || 'Successfully Created Job',
    })
    resetForm()
    onClose()
  }
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    handleSubmit,
    setFieldValue,
    isValid,
    submitCount,
    isSubmitting,
  } = useFormik({
    onSubmit: submitHandler,
    initialValues: initialData,
    enableReinitialize: true,
    validationSchema: jobSchema(type),
  })
  const { t } = useTranslation()
  const TypeHeaders = {
    internship: {
      update: 'Update Internship Program',
      create: 'Internship Program',
    },
    others: {
      update: 'Update Job',
      create: 'Create New Job',
    },
  }

  return (
    <Modal open={open} onClose={onClose} variant='md'>
      <div className='flex flex-col p-4 sm:p-6 gap-9 bg-white min-h-[700px] w-full'>
        <ModalHeader
          title={TypeHeaders[type][data ? 'update' : 'create']}
          onClose={onClose}
        />
        <form
          onSubmit={handleSubmit}
          className='max-h-[600px] flex flex-col gap-[33px] w-full'
        >
          <div className='max-h-full pb-4 overflow-x-hidden grid grid-cols-2 gap-4 w-full overflow-auto scrollbar !scrollbar-w-1 scrollbar-track-rounded-lg scrollbar-thumb-rounded-lg scrollbar-thumb-jaa-dark-teal-80 scrollbar-track-gray-100'>
            <FormGroup
              id='title'
              label={type === 'others' ? 'Job title' : 'Program title'}
              className='col-span-2'
            >
              <InputWithAdornment
                name='title'
                placeholder='Product Designer'
                value={values.title}
                error={
                  errors.title &&
                  (touched.title as unknown as string) &&
                  (errors.title as unknown as string)
                }
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormGroup>
            <FormGroup
              id='description'
              label='Description'
              className='col-span-2'
              subtext={`${values?.description?.length || 0} / 200`}
            >
              <Textarea
                name='description'
                className='select md !normal-case'
                rows={3}
                placeholder={`${t('app.say_something')}`}
                value={values.description}
                error={
                  errors.description &&
                  (touched.description as unknown as string) &&
                  (errors.description as unknown as string)
                }
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormGroup>
            {type === 'internship' ? (
              <FormGroup id='duration' label='Program Duration'>
                <SelectInput
                  className='select !h-12'
                  menuClassName='bg-white '
                  options={[
                    '3 months',
                    '6 months',
                    '9 months',
                    '12 months',
                    '1 year',
                    '1 year and 3 months',
                    '1 year and 6 months',
                  ]}
                  name='duration'
                  placeholder='Select'
                  value={values.duration}
                  error={
                    errors.duration &&
                    (touched.duration as unknown as string) &&
                    (errors.duration as unknown as string)
                  }
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </FormGroup>
            ) : (
              <FormGroup
                id='type'
                label='Job type'
                className='col-span-2 md:col-span-1'
              >
                <SelectInput
                  className='select !h-12'
                  menuClassName='bg-white '
                  options={(allFilters?.job_type || []).filter(
                    (el) => el.toLowerCase() !== 'internship'
                  )}
                  name='job_type'
                  placeholder='Select'
                  value={values.job_type}
                  error={
                    errors.job_type &&
                    (touched.job_type as unknown as string) &&
                    (errors.job_type as unknown as string)
                  }
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </FormGroup>
            )}
            <FormGroup
              id='work_policy'
              label='Work Policy'
              className='col-span-2 md:col-span-1'
            >
              <SelectInput
                className='select !h-12'
                menuClassName='bg-white '
                options={allFilters?.work_policy || []}
                name='work_policy'
                placeholder='Select'
                value={values.work_policy}
                error={
                  errors.work_policy &&
                  (touched.work_policy as unknown as string) &&
                  (errors.work_policy as unknown as string)
                }
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormGroup>
            <FormGroup
              id='experience'
              label='Years of Experience'
              className='col-span-2 md:col-span-1'
            >
              <InputWithAdornment
                type='number'
                name='years_of_experience'
                placeholder='Enter Numbers'
                min={0}
                value={values.years_of_experience}
                error={
                  errors.years_of_experience &&
                  (touched.years_of_experience as unknown as string) &&
                  (errors.years_of_experience as unknown as string)
                }
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormGroup>
            <FormGroup
              id='location'
              label='Location'
              className='col-span-2 md:col-span-1'
            >
              <InputWithAdornment
                name='location'
                placeholder='Enter Location'
                value={values.location}
                error={
                  errors.location &&
                  (touched.location as unknown as string) &&
                  (errors.location as unknown as string)
                }
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormGroup>
            <FormGroup id='pipeline' label='Pipeline' className='col-span-2'>
              <SelectInput
                className='select !h-12'
                menuClassName='bg-white min-w-[208px]'
                key={paginatedPipelines?.results?.length || -1}
                options={paginatedPipelines?.results || []}
                optionsValue={(item) => item?.id}
                optionsText={(item) => item?.name}
                name='pipeline'
                placeholder='Select'
                value={
                  String(values.pipeline) === '0' ? '' : String(values.pipeline)
                }
                error={
                  (isFetchingPipelines
                    ? ''
                    : paginatedPipelines?.results.length === 0
                    ? 'Pipeline is a required field. Please make sure you have created a pipeline to add to the job'
                    : '') ||
                  (errors.pipeline &&
                    (touched.pipeline as unknown as string) &&
                    (errors.pipeline as unknown as string))
                }
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormGroup>
            {type === 'internship' && (
              <FormGroup id='paid' className='col-span-2'>
                <ToggleSwitch
                  id='paid'
                  label='Position is paid'
                  className='w-10 gap-4'
                  activeColor='green'
                  onChange={(e) =>
                    handleChange({
                      ...e,
                      target: {
                        ...e.target,
                        name: 'paid',
                        value: e.currentTarget.checked,
                      },
                      currentTarget: {
                        ...e.currentTarget,
                        name: 'paid',
                        value: e.currentTarget.checked,
                      },
                    })
                  }
                  isChecked={!!values.paid}
                  onBlur={handleBlur}
                />
              </FormGroup>
            )}

            {((values.paid && type === 'internship') || type === 'others') && (
              <fieldset className='w-full col-span-2 gap-3 flex flex-col text-jaa-shades-gray-2.5 focus-within:text-jaa-gray-800 active:text-jaa-gray-800'>
                <section className='flex items-center justify-between w-full'>
                  <Typography as='legend'>Salary Range</Typography>
                </section>
                <section className='w-full grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <FormGroup id='min_salary_range' label='Minimum'>
                    <InputWithAdornment
                      name='min_salary_range'
                      placeholder='Enter'
                      value={values.min_salary_range}
                      error={
                        errors.min_salary_range &&
                        (touched.min_salary_range as unknown as string) &&
                        (errors.min_salary_range as unknown as string)
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormGroup>

                  <FormGroup id='max_salary_range' label='Maximum'>
                    <InputWithAdornment
                      name='max_salary_range'
                      placeholder='Enter'
                      value={values.max_salary_range}
                      error={
                        errors.max_salary_range &&
                        (touched.max_salary_range as unknown as string) &&
                        (errors.max_salary_range as unknown as string)
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormGroup>
                </section>
                <section className='w-full flex gap-4 items-center'>
                  <Typography
                    as='label'
                    htmlFor='currency'
                    className='xs text-[#979797]'
                  >
                    Currency:
                  </Typography>
                  <SelectInputV2
                    id='currency'
                    className='!h-[20px] !max-h-[30px] w-[90px] !py-0.5 !px-2 !rounded-full !border !border-jaa-shades-gray-1 bg-jaa-white'
                    menuClassName='bg-white !min-w-[140px]'
                    options={allFilters?.currency || []}
                    optionsText={(item) => item?.name}
                    optionsValue={(item) => item?.value}
                    optionsDisplay={(item) =>
                      item?.value?.split(' ')?.reverse()?.join(' ')
                    }
                    name='salary_currency'
                    placeholder='Select'
                    value={values.salary_currency}
                    hasSearch
                    error={
                      errors.salary_currency &&
                      (touched.salary_currency as unknown as string) &&
                      (errors.salary_currency as unknown as string)
                    }
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </section>
              </fieldset>
            )}
            <FormGroup
              id='closed_date'
              label='Closing date'
              className='col-span-2'
            >
              <InputWithAdornment
                name='closed_date'
                placeholder='Enter closing date'
                type='date'
                value={values.closed_date}
                error={
                  errors.closed_date &&
                  (touched.closed_date as unknown as string) &&
                  (errors.closed_date as unknown as string)
                }
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormGroup>
            <UploadImage
              values={{ images: values.images } as any}
              errors={errors}
              handleChange={handleChange}
              handleBlur={handleBlur}
              touched={touched as any}
              setFieldValue={setFieldValue}
              enableDefaultSelection={false}
              className='w-full col-span-2 pl-4 max-w-full'
            />
          </div>
          <div className='w-full flex items-center pt-3 pb-1 gap-2'>
            <Button
              type='submit'
              label={
                !data
                  ? 'POST '
                  : 'UPDATE ' + (type === 'internship' ? 'INTERNSHIP' : 'JOB')
              }
              loading={isSubmitting}
              disabled={isSubmitting || (submitCount > 0 && !isValid)}
              className='!uppercase flex-1 sm:flex-none'
            />
            <Button
              label='Cancel'
              onClick={onClose}
              disabled={isSubmitting}
              type='reset'
              className='!uppercase flex-1 sm:flex-none'
              variant='cancel'
            />
          </div>
        </form>
      </div>
    </Modal>
  )
}
const getInitialData = (
  data?: Job
): CreateJobDTO & {
  images: {
    file: undefined
    src: string
    id: number
  }[]
} => {
  if (!data) {
    return {
      requirements: [],
      closed_date: '',
      description: '',
      job_type: '',
      location: '',
      pipeline: 0,
      title: '',
      salary_currency: '',
      min_salary_range: '',
      max_salary_range: '',
      years_of_experience: 0,
      work_policy: '',
      images: [],
      duration: '',
      paid: false,
    }
  } else {
    return {
      ...data,
      pipeline: data.pipeline.id,
      images: data.images.map((image) => ({
        file: undefined,
        src: image.image,
        id: image.id,
      })),
    } as any
  }
}

export default CreateOrUpdateJob
